import { redirect } from 'react-router-dom'
import MatterLayout from '@/matter/components/MatterLayout'
import MatterOverview from '@/matter/components/matter-overview/MatterOverview'
import MatterSettingsPage from '@/matter/components/matter-settings/MatterSettingsPage'
import { createChronologyChildRoutes } from './chronology'
import { createTimelineChildRoutes } from './timeline'
import { createBillingSummaryChildRoutes } from './billingSummary'
import { createChatChildRoutes } from './chat'
import { createFilesChildRoutes } from './files'
import { createUserFilesChildRoutes } from './userFiles'
import { createReferenceViewerChildRoutes } from './referenceViewer'
import type { RouteFactory } from '@/routing/types'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import MatterFeatureGuard from '@/components/guards/MatterFeatureGuard'
import { canEditMatterSettings, canViewChats, canViewMedChron, canViewMedicalBills, canViewTimeline } from '@/matter/utils/matter-permissions'

/**
 * Creates matter-first routes under /dashboard/m/:matterId
 *
 * This factory creates the parent MatterLayout route with all feature
 * routes nested underneath. The matter context is provided by MatterLayout.
 *
 * URL structure:
 * - /dashboard/m/all → redirects to /dashboard/matters
 * - /dashboard/m/:matterId (overview/entry point)
 * - /dashboard/m/:matterId/settings
 * - /dashboard/m/:matterId/medical-chronology/...
 * - /dashboard/m/:matterId/billing-summaries/...
 * - /dashboard/m/:matterId/chat/:chatFeature/:chatId?
 * - /dashboard/m/:matterId/files/:driveName/*
 * - /dashboard/m/:matterId/user-files/:folderId?
 * - /dashboard/m/:matterId/reference-viewer/caselaw/:idType?/:id
 * - /dashboard/m/:matterId/reference-viewer/lrr/:s3Path
 * - /dashboard/m/:matterId/reference-viewer/legacy-file?viewFile=:relativePath
 *
 * @see MatterLayout for matter context management
 * @see Phase 4 of routing refactor plan
 */
export const createMatterRoutes: RouteFactory = () => [
  // Redirect /dashboard/m/all and all its children to /dashboard/matters
  {
    path: 'm/all/*',
    loader: () => redirect('/dashboard/matters'),
  },
  {
    path: 'm/all',
    loader: () => redirect('/dashboard/matters'),
  },
  {
    id: 'matter-layout',
    path: 'm/:matterId',
    element: <MatterLayout />,
    children: [
      // Index route - Matter overview as the entry point
      {
        index: true,
        element: <MatterOverview />,
      },
      ...(kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
        ? [
            {
              element: <MatterFeatureGuard canAccess={canEditMatterSettings} />,
              children: [
                {
                  path: 'settings',
                  element: <MatterSettingsPage />,
                },
              ],
            },
          ]
        : []),

      // Medical Chronology routes: /dashboard/m/:matterId/medical-chronology/...
      // Accepted collaborators are allowed when COLLABORATOR_MEDCHRON_ACCESS is on; otherwise
      // owner-only. Blocked viewers get the guard's "Coming soon" card.
      {
        element: <MatterFeatureGuard canAccess={canViewMedChron} />,
        children: createChronologyChildRoutes(),
      },

      // Timeline routes: /dashboard/m/:matterId/timeline/...
      // Owner-only until Timeline gets its own collaborator permission.
      {
        element: <MatterFeatureGuard canAccess={canViewTimeline} />,
        children: createTimelineChildRoutes(),
      },

      // Billing summary routes: /dashboard/m/:matterId/billing-summaries/...
      // Owner-only until Medical Bills gets its own collaborator permission.
      {
        element: <MatterFeatureGuard canAccess={canViewMedicalBills} />,
        children: createBillingSummaryChildRoutes(),
      },

      // Chat routes: /dashboard/m/:matterId/chat/:chatFeature/:chatId?
      // Available to owners and collaborators; the guard blocks non-members from deep-linking.
      {
        element: <MatterFeatureGuard canAccess={canViewChats} />,
        children: createChatChildRoutes(),
      },

      // Files routes: /dashboard/m/:matterId/files/:driveName/*
      ...createFilesChildRoutes(),

      // User Files routes: /dashboard/m/:matterId/user-files/:folderId?
      ...createUserFilesChildRoutes(),

      // Reference Viewer routes: /dashboard/m/:matterId/reference-viewer/...
      ...createReferenceViewerChildRoutes(),
    ],
  },
]
